import React, { FunctionComponent, useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { styles } from '../../../../styles/themes/style';
import Column from '../../../../components/Column';
import { useCampaignsRequest } from '../../../../services/apis/campaigns/useCampaignsRequest';
import { useAuthStore } from '../../../../stores/auth/useAuthStore';
import { AppRoutes } from '../../../../interfaces/store/appStore';
import { EmailPreference, IEmailPreflight } from '../../../../interfaces/models/campaign';
import FindEmailsModal from './FindEmailsModal';
import { useAppActions } from '../../../../stores/application/useAppActions';

/**
 * Address coverage for a campaign that has an email step.
 *
 * Shows what we hold right now, and a ceiling for finding the rest. The figure is
 * framed as "up to" because nothing is looked up to draw this panel — quoting an
 * exact number would mean querying the provider for every lead before the user has
 * agreed to spend anything, and it would still only be a guess.
 *
 * After a run the panel reports what was actually found and charged.
 */

/**
 * Coerce the payload into usable numbers.
 *
 * Every part of this panel is gated on numeric comparisons, so a field the server
 * didn't send reads as `undefined` and every `> 0` quietly fails — the card then
 * renders a heading and nothing else, with no error to explain it. That is exactly
 * what a stale bundle against a newer API produced. Filling in the gaps means the
 * panel degrades to a truthful figure instead of a blank.
 */
function normalizeCoverage(raw: any): IEmailPreflight | null {
  if (!raw || typeof raw !== 'object') return null;
  const num = (v: unknown, fallback = 0) => (Number.isFinite(Number(v)) ? Number(v) : fallback);

  const total = num(raw.total);
  const haveAddress = num(raw.haveAddress);

  return {
    ...raw,
    total,
    haveAddress,
    // Older payloads called this `findable`; without either, everything without an
    // address is still to look up.
    remaining: num(raw.remaining ?? raw.findable, Math.max(0, total - haveAddress)),
    estimatedCredits: num(raw.estimatedCredits),
    suppressed: num(raw.suppressed),
    blockedByEnrichmentOptOut: num(raw.blockedByEnrichmentOptOut),
    awaitingIdentifier: num(raw.awaitingIdentifier),
  };
}

/**
 * Shortest gap between two refocus-triggered re-checks. Credits and address counts
 * move on a human timescale; refetching on every window focus is churn, not freshness.
 */
const REFOCUS_MIN_INTERVAL_MS = 60 * 1000;

interface IProps {
  /**
   * Change this to force a re-check — the leads screen passes the enrolled total, so
   * adding leads to a running campaign updates the panel instead of leaving a stale
   * figure from before they arrived.
   */
  refreshKey?: number | string;
  campaignId: string;
  preference?: EmailPreference;
  /** Called after a successful enrichment run so the parent can refresh its leads. */
  onEnriched?: () => void;
}

const CampaignEmailCoverage: FunctionComponent<IProps> = ({ campaignId, preference, onEnriched, refreshKey }) => {
  const { getEmailPreflight, runEmailPreflight } = useCampaignsRequest();
  const { setToast } = useAppActions();
  const { store: { workspace } } = useAuthStore();

  const [data, setData]         = useState<IEmailPreflight | null>(null);
  const [loading, setLoading]   = useState(true);
  const [running, setRunning]   = useState(false);
  const [confirming, setConfirming] = useState(false);
  /** When the quote was last fetched, so refocus doesn't refetch on every alt-tab. */
  const lastLoadedAt = useRef(0);

  /**
   * @param background keeps the panel on screen while it refetches. A refresh the user
   *                   did not ask for must not blank out what they were reading.
   */
  const load = useCallback(async (background = false) => {
    if (!background) setLoading(true);
    const res: any = await getEmailPreflight(campaignId, preference);
    setData(normalizeCoverage(res?.data ?? res ?? null));
    lastLoadedAt.current = Date.now();
    setLoading(false);
    // getEmailPreflight is recreated per render by the request hook — depending on it
    // would re-fire this effect forever.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId, preference, refreshKey]);

  useEffect(() => { load(); }, [load]);

  /*
    Billing in its own tab.

    Two other routes were tried and neither holds. The enrichment modal's buy-credits
    mode white-screened when opened from here. Embedding the billing page in a dialog
    fares no better: it is built for a full-width page, so it is clipped on both sides,
    and "Proceed to payment" white-screens inside the modal — and fixing that means
    changing the billing page every other route depends on.

    A new tab leaves the campaign exactly as it was, which was the point of not
    navigating, and billing runs where it actually works. The refocus effect below
    re-checks the quote on return, so a top-up still shows up on its own.
  */
  const buyCredits = () => {
    window.open(AppRoutes.SETTINGS_BILLING, '_blank', 'noopener,noreferrer');
  };

  /**
   * Re-check the quote when the user comes back, so a top-up made elsewhere shows up
   * without a manual reload.
   *
   * Three guards, all learned the hard way: `focus` fires constantly — alt-tab,
   * devtools, a consent popup closing — so it is throttled and skipped while the
   * document is hidden, and the refetch runs in the background so the panel does not
   * blink through its loading state every time the window is touched.
   */
  useEffect(() => {
    const onFocus = () => {
      if (document.hidden) return;
      if (Date.now() - lastLoadedAt.current < REFOCUS_MIN_INTERVAL_MS) return;
      load(true);
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [load]);

  const handleEnrich = async () => {
    // Closed before the request, not after it. Waiting for the round-trip left the
    // dialog sitting there, which reads as if the click never registered — and the
    // panel behind it already shows "Finding…".
    setConfirming(false);
    setRunning(true);

    const res: any = await runEmailPreflight(campaignId, preference, true);
    const next = normalizeCoverage(res?.data ?? res ?? null);
    if (next) setData(next);
    setRunning(false);
    onEnriched?.();

    /*
      Report the outcome on a toast rather than a panel that stays put.

      The green box it replaces never said the two things that decide what to do next:
      how many leads still have no address, and whether anything was charged.
    */
    const found = next?.enrichedNow ?? 0;
    const stillMissing = Math.max(0, next?.remaining ?? 0);
    const attempted = found + stillMissing;

    if (next?.outOfCredits) {
      setToast({
        type: 'warning',
        message: `Found ${found} of ${attempted} · out of credits. Top up to find the rest.`,
      });
      return;
    }
    if (found === 0) {
      setToast({
        type: 'info',
        message: stillMissing > 0
          ? `No emails found. ${stillMissing} lead${stillMissing === 1 ? '' : 's'} still without an address — no credits used.`
          : 'No emails found — no credits used.',
      });
      return;
    }
    setToast({
      type: 'success',
      message: `Found ${found} of ${attempted} missing email${attempted === 1 ? '' : 's'} · ${found} credit${found === 1 ? '' : 's'} used.`
        + (stillMissing > 0 ? ` ${stillMissing} still without an address.` : ''),
    });
  };

  const openConfirm = () => setConfirming(true);

  if (loading) return <Wrap><Muted>Checking who we can email…</Muted></Wrap>;
  if (!data || data.total === 0) return null;

  const {
    total, haveAddress, remaining, estimatedCredits,
    suppressed, blockedByEnrichmentOptOut, awaitingIdentifier,
  } = data;
  const readyPct = total > 0 ? Math.round((haveAddress / total) * 100) : 0;

  const balance = Number(workspace?.enrichmentCredits ?? 0);
  const shortBy = Math.max(0, estimatedCredits - balance);

  return (
    <Wrap>
      <Column gap="10px">
        {/* Title and the action that acts on it share the top line — the lookup is
            the only thing anyone comes to this panel to do. */}
        <PanelHead>
          <Column gap="2px">
            <Title>Email coverage</Title>
            <Subtle>{balance} credit{balance === 1 ? '' : 's'} available</Subtle>
          </Column>
          {/* One button in both states. Whether there are enough credits is answered
              in the confirmation dialog, next to the cost — swapping this button for
              "Buy credits" hid the action the user actually came for. */}
          {remaining > 0 && !data.started && (
            <PrimaryBtn type="button" onClick={openConfirm} disabled={running}>
              {running ? 'Finding…' : 'Find missing emails'}
            </PrimaryBtn>
          )}
        </PanelHead>

        <BarRow>
          <Bar>
            <BarSeg $pct={readyPct} $tone="ready" />
          </Bar>
          <BarPct>{readyPct}%</BarPct>
        </BarRow>

        <Legend>
          <LegendItem>
            <Dot $tone="ready" />
            <strong>{haveAddress}</strong> of {total} leads have an email
          </LegendItem>
          {remaining > 0 && (
            <LegendItem>
              <Dot $tone="none" />
              <strong>{remaining}</strong> email{remaining === 1 ? '' : 's'} to be found · Estimated
              cost: up to <strong>{estimatedCredits}</strong> credit{estimatedCredits === 1 ? '' : 's'}
              {shortBy === 0 && <BuyLink type="button" onClick={buyCredits}>Buy credits</BuyLink>}
            </LegendItem>
          )}
        </Legend>

        {/* Named rather than left out: a Sales Navigator import has no LinkedIn URL
            until its vanity resolves, and silently dropping those rows is what makes
            the panel look broken when the numbers don't add up to the total. */}
        {awaitingIdentifier > 0 && (
          <Muted>
            {awaitingIdentifier} lead{awaitingIdentifier === 1 ? '' : 's'} still resolving their LinkedIn
            profile — they can be looked up once that finishes.
          </Muted>
        )}

        {(suppressed > 0 || blockedByEnrichmentOptOut > 0) && (
          <Muted>
            {suppressed > 0 && <>{suppressed} unsubscribed or suppressed. </>}
            {blockedByEnrichmentOptOut > 0 && <>{blockedByEnrichmentOptOut} opted out of data lookups.</>}
          </Muted>
        )}

        {/* The cost, the balance and the shortfall now live together in the
            confirmation dialog, where the decision is actually made. */}

        {/* The outcome is a toast now — see handleEnrich. Only the one thing a toast
            cannot carry stays here: that a background lookup is still running. */}
        {data.started && data.enrichedTransactionId && (
          <SuccessNote>
            The rest are being looked up and will appear on the leads shortly.
          </SuccessNote>
        )}

        {remaining === 0 && haveAddress === 0 && (
          <WarnNote>
            None of these leads have an email address we can find. The email step will be skipped for
            everyone — consider removing it, or switching the step to allow personal addresses.
          </WarnNote>
        )}
      </Column>

      <FindEmailsModal
        show={confirming}
        onHide={() => setConfirming(false)}
        onConfirm={handleEnrich}
        remaining={remaining}
        estimatedCredits={estimatedCredits}
        balance={balance}
        running={running}
        onBuyCredits={buyCredits}
      />
    </Wrap>
  );
};

export default CampaignEmailCoverage;

// ─── Styles ───────────────────────────────────────────────────────────────────

const ACCENT = styles.colors.primary600;

const Wrap = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 14px 16px;
  background: #fff;
`;

const Title = styled.div`
  font-family: NunitoSans; font-size: 13px; font-weight: 700; color: #1e293b;
`;

const PanelHead = styled.div`
  display: flex; align-items: flex-start; justify-content: space-between; gap: 12px;
`;

const Subtle = styled.div`
  font-family: NunitoSans; font-size: 11px; color: #94a3b8;
`;

const BarRow = styled.div`
  display: flex; align-items: center; gap: 10px;
`;

const BarPct = styled.span`
  flex: 0 0 auto;
  font-family: NunitoSans; font-size: 11px; font-weight: 600; color: #64748b;
`;

// Plain block rather than a flex row: there is only one segment now, and a flex
// child's width is easy to have stretched by a stray grow/basis rule.
const Bar = styled.div`
  flex: 1 1 auto;
  position: relative; height: 8px; border-radius: 999px; overflow: hidden;
  background: #f1f5f9;
`;

const BarSeg = styled.div<{ $pct: number; $tone: 'ready' | 'findable' }>`
  height: 100%;
  width: ${p => Math.max(0, Math.min(100, p.$pct))}%;
  background: ${p => (p.$tone === 'ready' ? '#16a34a' : '#a78bfa')};
  transition: width .2s ease;
`;

const Legend = styled.div`
  display: flex; flex-wrap: wrap; gap: 6px 16px;
`;

const LegendItem = styled.div`
  display: flex; align-items: center; gap: 6px;
  font-family: NunitoSans; font-size: 12px; color: #475569;
  strong { color: #0f172a; }
`;

const Dot = styled.span<{ $tone: 'ready' | 'findable' | 'none' }>`
  width: 8px; height: 8px; border-radius: 999px;
  background: ${p => (p.$tone === 'ready' ? '#16a34a' : p.$tone === 'findable' ? '#a78bfa' : '#cbd5e1')};
`;

const Muted = styled.div`
  font-family: NunitoSans; font-size: 11px; color: #94a3b8; line-height: 1.5;
`;

/* Base for the after-the-fact notes. It used to double as the inline confirmation
   box; that moved into the dialog, so only the outcome notes build on it now. */
const NoteBase = styled.div`
  font-family: NunitoSans; font-size: 12px; color: #475569; line-height: 1.55;
  background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 10px 12px;
  strong { color: #0f172a; }
`;

const SuccessNote = styled(NoteBase)`
  background: #f0fdf4; border-color: #bbf7d0; color: #166534;
`;

const WarnNote = styled(NoteBase)`
  background: #fffbeb; border-color: #fde68a; color: #92400e;
`;

const PrimaryBtn = styled.button`
  border: none; border-radius: 8px; padding: 8px 14px;
  background: ${ACCENT}; color: #fff; cursor: pointer;
  font-family: NunitoSans; font-size: 12px; font-weight: 700;
  &:disabled { opacity: .6; cursor: default; }
`;

const BuyLink = styled.button`
  align-self: center; margin-left: 6px;
  border: none; background: none; padding: 0; cursor: pointer;
  color: ${ACCENT}; text-decoration: underline;
  font-family: NunitoSans; font-size: 12px; font-weight: 600;
`;
